export enum ArticleType {
  TEXT = 'text',
  EMBED = 'embed',
}

export enum EmbedKind {
  DOCUMENT = 'document',
  VIDEO = 'video',
}

/** Max size (in bytes) accepted for an uploaded article cover image. */
export const ARTICLE_COVER_MAX_BYTES = 2 * 1024 * 1024;

/** File extensions accepted for an article cover image upload. */
export const ARTICLE_COVER_FILE_TYPES = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'];

/** Domains allowed as the source of an embedded resource, per embed kind (these or their subdomains). */
export const ARTICLE_EMBED_ALLOWED_DOMAINS: Record<EmbedKind, string[]> = {
  [EmbedKind.DOCUMENT]: ['drive.google.com', 'canva.com', 'genially.com'],
  [EmbedKind.VIDEO]: ['drive.google.com', 'synthesia.io', 'youtube.com', 'youtu.be', 'vimeo.com'],
};

/** Human-readable source names allowed per embed kind, for placeholders and error messages. */
export const ARTICLE_EMBED_ALLOWED_SOURCES: Record<EmbedKind, string[]> = {
  [EmbedKind.DOCUMENT]: ['Google Drive', 'Canva', 'Genially'],
  [EmbedKind.VIDEO]: ['Google Drive', 'Synthesia', 'YouTube', 'Vimeo'],
};

/** Whether an embed URL points to a domain allowed for the given embed kind (or a subdomain of one). */
export function isAllowedEmbedUrl(url: string, kind: EmbedKind): boolean {
  let hostname: string;
  try {
    hostname = new URL(url).hostname.toLowerCase();
  } catch {
    return false;
  }
  const domains = ARTICLE_EMBED_ALLOWED_DOMAINS[kind] ?? [];
  return domains.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`));
}

export interface Article {
  id: number;
  title: string;
  type: ArticleType;
  content: string | null;
  embedUrl: string | null;
  embedKind: EmbedKind | null;
  readingTime: number | null;
  coverImage: string | null;
  createdAt: string;
  updatedAt: string;
}

export type ArticleSummary = Omit<Article, 'content'>;

export interface CreateArticleInput {
  title: string;
  type: ArticleType;
  content?: string | null;
  embedUrl?: string | null;
  embedKind?: EmbedKind | null;
  readingTime?: number | null;
  coverImage?: string | null;
}

export interface UpdateArticleInput {
  title?: string;
  content?: string | null;
  embedUrl?: string | null;
  embedKind?: EmbedKind | null;
  readingTime?: number | null;
  coverImage?: string | null;
}
